// 轻量自研 i18n（避开 i18next ~30KB 依赖）
//
// 设计：
// - createContext + useT() / useLocale()
// - 翻译表静态 import（按需 split 留待将来用 dynamic import）
// - 启动时 detect 顺序：localStorage > navigator.language > 'en'
// - bootstrap script 在 index.html 同步 set <html lang>，防 FOUC

import * as React from 'react';
import { en } from './locales/en';
import { zhCN } from './locales/zh-CN';
import { zhTW } from './locales/zh-TW';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { es } from './locales/es';
import { fr } from './locales/fr';
import { de } from './locales/de';
import { DEFAULT_LOCALE, LOCALES, type Locale, type TranslationDict } from './types';

const TRANSLATIONS: Record<Locale, TranslationDict> = {
  en,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  ja,
  ko,
  es,
  fr,
  de,
};

const STORAGE_KEY = 'doneline-locale-v1';

// ---------- detection ----------

export function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    // ignore
  }

  const navLang = navigator.language || (navigator.languages?.[0] ?? 'en');
  // 完整匹配先（zh-CN / zh-TW）
  if ((LOCALES as readonly string[]).includes(navLang)) return navLang as Locale;

  // base 匹配（zh-* → zh-CN，ja-JP → ja，en-US → en）
  const base = navLang.split('-')[0];
  if (base === 'zh') return 'zh-CN';
  if ((LOCALES as readonly string[]).includes(base ?? '')) return base as Locale;

  return DEFAULT_LOCALE;
}

// ---------- translation lookup ----------

function getNested(obj: TranslationDict, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, k) => {
    if (acc && typeof acc === 'object' && k in (acc as object)) {
      return (acc as Record<string, unknown>)[k];
    }
    return undefined;
  }, obj);
}

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k];
    return v === undefined || v === null ? `{${k}}` : String(v);
  });
}

function lookup(locale: Locale, key: string): string | undefined {
  const v = getNested(TRANSLATIONS[locale], key);
  if (typeof v === 'string') return v;
  return undefined;
}

// ---------- context ----------

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LocaleContext = React.createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(() => detectInitialLocale());

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore
    }
  }, [locale]);

  const t = React.useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      const direct = lookup(locale, key);
      if (direct !== undefined) return interpolate(direct, vars);
      // fallback 到英语
      const fallback = lookup('en', key);
      if (fallback !== undefined) return interpolate(fallback, vars);
      // 最终兜底：返回 key 本身（开发期能立刻看出缺失）
      return key;
    },
    [locale],
  );

  const value = React.useMemo<LocaleContextValue>(
    () => ({ locale, setLocale: setLocaleState, t }),
    [locale, t],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = React.useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

export function useT() {
  return useLocale().t;
}

export type { Locale };
export { LOCALES, LOCALE_LABELS, DEFAULT_LOCALE } from './types';
